import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Row, Spin, Switch } from 'antd';

import CustomMaskedInput from 'components/MaskedInput/index';
import { clearMoneyMask } from 'helpers/functions';
import customerService from 'v2/services/cutomer';
import { useUserStore } from 'v2/store/User';
import { ruleRunner, run } from 'validations/ruleRunner';
import { required, isEmail } from 'validations/rules';

const Validations = [ruleRunner('email', 'e-mail do co-afiliado', required, isEmail)];

interface IProps {
  coaffiliates: any;
  onChangeCheck: any;
  setReadyToSave: any;
  handleChangeCoAffiliates: any;
  public: any;
}

const Coaffiliates: React.FC<IProps> = ({
  coaffiliates: coaffiliatesProp,
  onChangeCheck,
  setReadyToSave,
  handleChangeCoAffiliates,
  public: isPublic
}) => {
  const [coaffiliates, setCoaffiliates] = useState<any[]>(JSON.parse(JSON.stringify(coaffiliatesProp)));
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [canDelete, setCanDelete] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  const { currentUserId } = useUserStore(({ user }) => ({ currentUserId: user.id }));

  useEffect(() => {
    setCoaffiliates(JSON.parse(JSON.stringify(coaffiliatesProp || [])));
  }, [coaffiliatesProp]);

  const onChangeEmail = useCallback(
    (e: any, index: any) => {
      const newCoaffiliates = [...coaffiliates];
      newCoaffiliates[index] = { ...newCoaffiliates[index], email: e.target.value };
      const errors = run({ email: e.target.value }, Validations);

      setCoaffiliates(newCoaffiliates);
      setValidationErrors(errors);
      setReadyToSave(false);
      setCanDelete(false);
    },
    [coaffiliates, setReadyToSave]
  );

  const onChangePercent = useCallback(
    (value: any, index: any) => {
      const newCoaffiliates = [...coaffiliates];
      newCoaffiliates[index] = { ...newCoaffiliates[index], percent: clearMoneyMask(value) };

      handleChangeCoAffiliates(newCoaffiliates);
      setCoaffiliates(newCoaffiliates);
    },
    [coaffiliates, handleChangeCoAffiliates]
  );

  const onSearchCustomerByEmail = useCallback(
    async (email: any, index: any) => {
      setIsSearching(true);
      const customerData = await customerService.loadCustomerByEmail(email);
      const found = customerData?.length > 0 ? customerData[0] : null;
      setCanDelete(true);
      setIsSearching(false);

      if (!found) {
        toast.error('Insira um email válido!');
      } else {
        const newCoaffiliates = [...coaffiliates];

        if (newCoaffiliates[index]) {
          newCoaffiliates[index] = {
            ...newCoaffiliates[index],
            name: found.name,
            customer_id: found.id,
            disabled: currentUserId === found.id
          };
          handleChangeCoAffiliates(newCoaffiliates);
          setCoaffiliates(newCoaffiliates);
          setReadyToSave(true);
        }
      }
    },
    [coaffiliates, currentUserId, handleChangeCoAffiliates, setReadyToSave]
  );

  const deleteCoaffiliate = useCallback(
    (k: any) => {
      const filtered = coaffiliates.filter((_: any, index: any) => index !== k);
      handleChangeCoAffiliates(filtered);
      setCoaffiliates(filtered);
      setReadyToSave(true);
    },
    [coaffiliates, handleChangeCoAffiliates, setReadyToSave]
  );

  const handleChangePublic = useCallback(
    (checked: boolean) => {
      let newCoaffiliates = [...coaffiliates];

      if (checked) {
        newCoaffiliates.unshift({
          customer_id: 123456,
          percent: 0,
          name: 'Parceiros de Divulgação [Co-Afiliado]',
          email: null
        });
      } else {
        newCoaffiliates = newCoaffiliates.filter((value: any) => value.customer_id !== 123456);
      }

      onChangeCheck('public', { target: { checked } });
      handleChangeCoAffiliates(newCoaffiliates);
      setCoaffiliates(newCoaffiliates);
    },
    [coaffiliates, onChangeCheck, handleChangeCoAffiliates]
  );

  const addNewEmpty = useCallback(() => {
    const newCoaffiliates = [
      ...coaffiliates,
      {
        customer_id: 0,
        percent: 0,
        name: 'Busque seu co-afiliado',
        email: ''
      }
    ];
    handleChangeCoAffiliates(newCoaffiliates);
    setCoaffiliates(newCoaffiliates);
  }, [coaffiliates, handleChangeCoAffiliates]);

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const isValid = useCallback(
    (field: any) => {
      return validationErrors[field] ? true : false;
    },
    [validationErrors]
  );

  const coaffiliatesList = coaffiliates.map((item: any, index: any) => {
    if (item.customer_id === 123456) {
      return (
        <Row key={'coafi' + index} align='middle' className='bg-grey-100 rounded px-4 py-2'>
          <Col span={20}>
            <label>{item.name}</label>
          </Col>
          <Col span={4}>
            <CustomMaskedInput
              id={`campaign-form-coaffiliate-percent-${index}`}
              mask='percent'
              startAdornment='%'
              fullWidth
              disableUnderline={true}
              value={item.percent}
              InputLabelProps={{
                shrink: true
              }}
              onChange={(value: any) => onChangePercent(value, index)}
            />
          </Col>
        </Row>
      );
    } else if (currentUserId === item.customer_id && !item?.disabled) {
      return (
        <Row key={'coafi' + index} align='middle' gutter={[16, 16]} className='my-8'>
          <Col span={8}>
            <label className='mb-1 block text-sm'>E-mail do co-afiliado</label>
            <Input
              id={`campaign-form-coaffiliate-email-${index}`}
              disabled
              value={item.email}
              suffix={<SearchOutlined style={{ opacity: 0.3 }} />}
            />
          </Col>
          <Col span={8}>
            <label className='mb-1 block text-sm'>Nome do co-afiliado</label>
            <Input id={`campaign-form-coaffiliate-name-${index}`} disabled value={item.name} />
          </Col>
          <Col span={6}>
            <CustomMaskedInput
              id={`campaign-form-coaffiliate-percent-${index}`}
              mask='percent'
              label='Porcentagem'
              startAdornment='%'
              fullWidth
              disableUnderline={true}
              value={item.percent}
              InputLabelProps={{ shrink: true }}
              onChange={(value: any) => onChangePercent(value, index)}
            />
          </Col>
        </Row>
      );
    } else {
      return (
        <Row key={'coafi' + index} align='middle' gutter={[16, 16]} className='my-8'>
          <Col span={8}>
            <label className='mb-1 block text-sm'>E-mail do co-afiliado</label>
            <Input
              id={`campaign-form-coaffiliate-email-${index}`}
              disabled={isSearching}
              value={item.email}
              onBlur={() => onSearchCustomerByEmail(item.email, index)}
              onChange={(e: any) => onChangeEmail(e, index)}
              status={isValid('email') ? 'error' : undefined}
              suffix={
                isSearching ? (
                  <Spin size='small' />
                ) : (
                  <Button
                    id={`campaign-form-coaffiliate-search-${index}`}
                    type='text'
                    size='small'
                    disabled={isValid('email') || !item.email}
                    onClick={() => onSearchCustomerByEmail(item.email, index)}
                    icon={<SearchOutlined />}
                  />
                )
              }
            />
            {isValid('email') && <span className='text-xs text-red-500'>{errorFor('email')}</span>}
          </Col>
          <Col span={8}>
            <label className='mb-1 block text-sm'>Nome do co-afiliado</label>
            <Input id={`campaign-form-coaffiliate-name-${index}`} disabled value={item.name} />
          </Col>
          <Col span={6}>
            <CustomMaskedInput
              id={`campaign-form-coaffiliate-percent-${index}`}
              mask='percent'
              label='Porcentagem'
              startAdornment='%'
              fullWidth
              disableUnderline={true}
              value={item.percent}
              InputLabelProps={{ shrink: true }}
              onChange={(value: any) => onChangePercent(value, index)}
            />
          </Col>
          <Col span={2}>
            <Button
              id={`campaign-form-coaffiliate-delete-${index}`}
              type='text'
              shape='circle'
              disabled={!canDelete}
              danger
              onClick={() => deleteCoaffiliate(index)}
              icon={<DeleteOutlined />}
              className='mt-6!'
            />
          </Col>
        </Row>
      );
    }
  });

  return (
    <>
      <Row justify='space-between' align='middle'>
        <Col className={'py-2 pr-4'}>
          <div className='flex items-center gap-2'>
            <Switch checked={isPublic} onChange={handleChangePublic} />
            <span>Essa campanha é pública?</span>
          </div>
        </Col>
        <Col>
          {coaffiliates.filter((item: any) => item.customer_id !== 123456 && currentUserId !== item.customer_id)
            .length === 0 && (
            <Button id='campaign-form-coaffiliate-add' type='primary' size='small' onClick={addNewEmpty}>
              Novo
            </Button>
          )}
        </Col>
      </Row>
      {coaffiliatesList}
    </>
  );
};

export default Coaffiliates;
